/**
 * One-line "why this job matches you" reason for a personalized feed row.
 *
 * D-3 finding (2026-07-15 value-delivery audit): card→click CTR is 1.9%, the
 * funnel bottleneck — NOT click→render (49.9% healthy). Salary already ships
 * on every feed card; the remaining clickability lever is a concrete, personal
 * "why" string on rows the feed actually ranked against the viewer's profile.
 *
 * This formatter turns the personalization-score breakdown into a single,
 * honest, localized reason by picking the dimension where the job matches
 * the user's profile most strongly RELATIVE to that dimension's weight — so a
 * full skills overlap (25/25) can outrank a half category match (20/40),
 * surfacing the most specific signal instead of always defaulting to the
 * highest-ceiling dimension.
 *
 * PRODUCT INVARIANT — read before changing.
 * `fit` (Indonesia suitability) is deliberately EXCLUDED. It is the baseline
 * promise of the whole board, not a per-row attribute worth a badge (see
 * `src/lib/jobs/fit-blocker.ts` — "never add a positive/affirming fit badge").
 * `total` is the sum, not a dimension. Only {category, skills, seniority,
 * jobType, remoteType} are eligible reasons.
 *
 * Honesty contract:
 *  - Returns `null` when the breakdown is missing or every eligible dimension
 *    is 0. The cold-start path (`getColdStartFeed`) attaches a `nullBreakdown`
 *    of all zeros with `isPersonalizedFit: false`, so an anonymous / no-profile
 *    row renders NO reason rather than a fabricated one.
 *  - jobType / remoteType are binary (0 or full weight) → ratio 0 or 1.0, so a
 *    non-zero reason is always a real preference match, never an oversell.
 *
 * Pure (no I/O, no AI, no locale file) — safe on the render path. The caller
 * gates on `isPersonalizedFit === true` so the reason only ever appears on rows
 * that were genuinely ranked against the viewer's profile.
 */

import type { Locale } from "@/lib/i18n/config";
import type { ScoreBreakdown } from "@/lib/personalization/score";

// Weight ceilings per dimension. Mirror the constants in `score.ts`; kept
// local so this helper stays a pure leaf with no import-time coupling to the
// scoring module's internals beyond the shared `ScoreBreakdown` type.
const CATEGORY_WEIGHT = 40;
const SKILLS_WEIGHT = 25;
const SENIORITY_WEIGHT = 20;
const JOB_TYPE_WEIGHT = 5;
const REMOTE_TYPE_WEIGHT = 5;

type MatchDimension =
	| "skills"
	| "category"
	| "seniority"
	| "jobType"
	| "remoteType";

// Order is the tie-break: when two dimensions share the top ratio, the earlier
// entry wins. `skills` is most specific to the user (their own extracted
// skills), so it outranks the broader `category` on a tie.
const DIMENSIONS: ReadonlyArray<{ key: MatchDimension; weight: number }> = [
	{ key: "skills", weight: SKILLS_WEIGHT },
	{ key: "category", weight: CATEGORY_WEIGHT },
	{ key: "seniority", weight: SENIORITY_WEIGHT },
	{ key: "jobType", weight: JOB_TYPE_WEIGHT },
	{ key: "remoteType", weight: REMOTE_TYPE_WEIGHT },
];

const LABELS: Record<MatchDimension, Record<Locale, string>> = {
	skills: { id: "Cocok dengan skill kamu", en: "Matches your skills" },
	category: { id: "Sesuai bidang kamu", en: "Matches your target field" },
	seniority: { id: "Level senioritas cocok", en: "Right seniority level" },
	jobType: { id: "Sesuai jenis kerja kamu", en: "Matches your work type" },
	remoteType: {
		id: "Sesuai preferensi remote kamu",
		en: "Matches your remote preference",
	},
};

/**
 * Pick the dominant personalization dimension and return a localized 1-line
 * reason, or `null` when no eligible dimension scored (cold-start / no
 * profile / only the suppressed `fit` dimension matched).
 */
export function formatMatchReason(
	breakdown: ScoreBreakdown | null | undefined,
	locale: Locale,
): string | null {
	if (!breakdown) return null;

	let best: { key: MatchDimension; ratio: number } | null = null;
	for (const dim of DIMENSIONS) {
		const raw = breakdown[dim.key];
		if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) {
			continue;
		}
		const ratio = raw / dim.weight;
		// Strict `>` keeps the first-seen (array-order) winner on a tie.
		if (best === null || ratio > best.ratio) {
			best = { key: dim.key, ratio };
		}
	}

	if (best === null || best.ratio <= 0) return null;
	return LABELS[best.key][locale];
}
